import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { messagesService } from '../services/api-service';
import { MessageCircle, Send, Inbox, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Messages.css';

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('conversations'); // 'conversations' or 'messages'
  const [newMessage, setNewMessage] = useState({
    recipientId: '',
    subject: '',
    body: ''
  });
  const [sending, setSending] = useState(false);
  const [showComposer, setShowComposer] = useState(false);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      loadConversationMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await messagesService.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      // For now, load all messages and filter by conversation
      // In a real implementation, you'd have a specific endpoint
      const allMessages = await messagesService.getMyMessages();
      const conversationMessages = allMessages.filter(
        m => m.conversationId === conversationId || 
        (m.senderId === user.id && m.recipientId === selectedConversation.otherUserId) ||
        (m.recipientId === user.id && m.senderId === selectedConversation.otherUserId)
      );
      setMessages(conversationMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)));
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.recipientId || !newMessage.body) {
      alert('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      await messagesService.sendMessage(newMessage);
      setNewMessage({ recipientId: '', subject: '', body: '' });
      setShowComposer(false);
      // Reload conversations
      await loadConversations();
      alert('Message sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-AU', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    }
  };

  if (!user) {
    return (
      <div className="messages-page">
        <div className="container">
          <div className="messages-login-required">
            <MessageCircle size={64} />
            <h2>Login Required</h2>
            <p>Please log in to view your messages.</p>
            <Link to="/?login=true" className="btn">
              Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-page">
      {/* Header */}
      <section className="messages-header">
        <div className="container">
          <div className="messages-header-content">
            <Link to="/" className="messages-back">
              <ArrowLeft size={20} />
              Back
            </Link>
            <div className="messages-header-main">
              <MessageCircle size={48} className="messages-header-icon" />
              <h1>Messages</h1>
            </div>
            <button
              onClick={() => setShowComposer(true)}
              className="btn btn-orange"
            >
              <Send size={20} />
              New Message
            </button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="messages-content section">
        <div className="container">
          <div className="messages-layout">
            {/* Conversations List */}
            <div className="messages-sidebar">
              <div className="messages-sidebar-header">
                <Inbox size={20} />
                <h2>Conversations</h2>
              </div>
              {loading ? (
                <div className="messages-loading">
                  <p>Loading conversations...</p>
                </div>
              ) : conversations.length > 0 ? (
                <div className="messages-conversations-list">
                  {conversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      className={`messages-conversation-item ${
                        selectedConversation?.id === conversation.id ? 'active' : ''
                      }`}
                      onClick={() => setSelectedConversation(conversation)}
                    >
                      <div className="messages-conversation-avatar">
                        <User size={20} />
                      </div>
                      <div className="messages-conversation-info">
                        <div className="messages-conversation-header">
                          <span className="messages-conversation-name">
                            {conversation.otherUserName || 'Unknown User'}
                          </span>
                          {conversation.unreadCount > 0 && (
                            <span className="messages-unread-badge">
                              {conversation.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="messages-conversation-preview">
                          {conversation.lastMessage?.substring(0, 50) || 'No messages yet'}
                          {conversation.lastMessage?.length > 50 ? '...' : ''}
                        </p>
                        {conversation.lastMessageDate && (
                          <span className="messages-conversation-date">
                            {formatDate(conversation.lastMessageDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="messages-empty">
                  <p>No conversations yet</p>
                  <button
                    onClick={() => setShowComposer(true)}
                    className="btn btn-small"
                  >
                    Start a conversation
                  </button>
                </div>
              )}
            </div>

            {/* Messages View */}
            <div className="messages-main">
              {selectedConversation ? (
                <>
                  <div className="messages-thread-header">
                    <div className="messages-thread-user">
                      <User size={24} />
                      <h3>{selectedConversation.otherUserName || 'Unknown User'}</h3>
                    </div>
                  </div>
                  <div className="messages-thread">
                    {messages.length > 0 ? (
                      messages.map((message) => (
                        <div
                          key={message.id}
                          className={`messages-message ${
                            message.senderId === user.id ? 'sent' : 'received'
                          }`}
                        >
                          <div className="messages-message-content">
                            {message.subject && (
                              <div className="messages-message-subject">
                                {message.subject}
                              </div>
                            )}
                            <p>{message.body}</p>
                            <span className="messages-message-time">
                              {formatDate(message.createdAt)}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="messages-empty-thread">
                        <p>No messages in this conversation yet</p>
                      </div>
                    )}
                  </div>
                  <div className="messages-compose">
                    <form onSubmit={handleSendMessage}>
                      <textarea
                        placeholder="Type your message..."
                        value={newMessage.body}
                        onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
                        rows="3"
                        required
                      />
                      <button type="submit" className="btn btn-orange" disabled={sending}>
                        <Send size={18} />
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="messages-select-conversation">
                  <MessageCircle size={64} />
                  <h3>Select a conversation</h3>
                  <p>Choose a conversation from the list to view messages</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* New Message Composer Modal */}
      {showComposer && (
        <div className="messages-modal-overlay" onClick={() => setShowComposer(false)}>
          <div className="messages-modal" onClick={(e) => e.stopPropagation()}>
            <h2>New Message</h2>
            <form onSubmit={handleSendMessage}>
              <div className="messages-form-group">
                <label htmlFor="recipient">Recipient User ID *</label>
                <input
                  type="text"
                  id="recipient"
                  value={newMessage.recipientId}
                  onChange={(e) => setNewMessage({ ...newMessage, recipientId: e.target.value })}
                  placeholder="Enter user ID"
                  required
                />
                <small>Note: In a full implementation, this would be a user selector</small>
              </div>
              <div className="messages-form-group">
                <label htmlFor="subject">Subject (optional)</label>
                <input
                  type="text"
                  id="subject"
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                  placeholder="Message subject"
                />
              </div>
              <div className="messages-form-group">
                <label htmlFor="body">Message *</label>
                <textarea
                  id="body"
                  value={newMessage.body}
                  onChange={(e) => setNewMessage({ ...newMessage, body: e.target.value })}
                  placeholder="Type your message..."
                  rows="5"
                  required
                />
              </div>
              <div className="messages-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowComposer(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-orange" disabled={sending}>
                  {sending ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;


